<script setup>
import {ref, reactive, watch, nextTick, onMounted} from 'vue'
import {useRoute} from 'vue-router'
import logo from '/creativecms-logo.png'
import api from '@/services/api'
import {useToastsStore} from '@/stores/toasts'

const route = useRoute()
const toastsStore = useToastsStore()

// --- State ---

const view = ref('')
const loginFormData = reactive({
    username: '',
    password: '',
    email: ''
})

const forgotPasswordEmail = ref(null)

// --- Methods ---

const loginUser = (user) => {
    // Current implementation: hard reload to clear legacy states/navigation issues
    window.location.reload()
}

const autoLogin = async (loginToken) => {
    const existingToken = localStorage.getItem('token')

    if (existingToken) {
        localStorage.removeItem('token')
        window.location.reload()
        return
    }

    try {
        const obj = await api.post('api/auto-login', {
            key: loginToken,
        })
        localStorage.setItem('token', obj.data.token)
        loginUser(obj.data.user)
        window.location.href = '/'
    } catch (obj) {
        if (obj.status === 409) view.value = 'password'
    }
}

const processForm = async () => {
    if (!loginFormData.username) {
        return toastsStore.add({title: 'Error', body: 'Please enter a username.'})
    }
    if (!loginFormData.password) {
        return toastsStore.add({title: 'Error', body: 'Please enter a password.'})
    }

    const obj = await api.post('api/login', {
        username: loginFormData.username,
        password: loginFormData.password,
    })

    if (obj.data.token) localStorage.setItem('token', obj.data.token)
    if (obj.data.user) loginUser(obj.data.user)
}

const processPasswordForm = async () => {
    if (!loginFormData.email) {
        return toastsStore.add({title: 'Error', body: 'Please enter an email address.'})
    }

    await api.post('api/login/forgot-password', {
        email: loginFormData.email,
    })
    view.value = 'password-email-sent'
}

// --- Lifecycle ---

onMounted(() => {
    if (route.query.login) {
        autoLogin(route.query.login)
    } else if (!localStorage.getItem('token')) {
        view.value = 'login'
    }
})

// --- Watchers ---

watch(view, (newView) => {
    if (newView === 'password') {
        nextTick(() => {
            forgotPasswordEmail.value?.focus()
        })
    }
})
</script>

<template>
    <section class="row text-center">
        <div class="offset-3 col-6 offset-sm-4 col-sm-4 offset-md-5 col-md-2 pt-5 pb-4">
            <img :src="logo" alt="CreativeCMS Logo" class="mt-2 w-100"/>
        </div>

        <form
            v-if="view === 'login'"
            class="col-10 offset-1 col-sm-6 offset-sm-3 col-md-4 offset-md-4 col-lg-4 offset-lg-4 col-xl-2 offset-xl-5"
            @submit.prevent="processForm"
        >
            <div class="mb-4">
                <label class="visually-hidden form-label">Username</label>
                <input
                    v-model="loginFormData.username"
                    v-focus
                    autocapitalize="off"
                    autocorrect="off"
                    class="form-control input-lg"
                    name="username"
                    placeholder="Username"
                    type="text"
                >
            </div>

            <div class="mb-4">
                <label class="visually-hidden form-label">Password</label>
                <input
                    v-model="loginFormData.password"
                    autocomplete="off"
                    class="form-control input-lg"
                    name="password"
                    placeholder="Password"
                    type="password"
                >
            </div>

            <div class="mb-4">
                <button class="btn btn-primary w-100">Sign in</button>
            </div>

            <div class="mb-4">
                <button class="btn btn-sm btn-light" type="button" @click="view = 'password'">
                    Forgot password?
                </button>
            </div>
        </form>

        <form
            v-else-if="view === 'password'"
            class="col-10 offset-1 col-sm-6 offset-sm-3 col-md-4 offset-md-4 col-lg-4 offset-lg-4 col-xl-2 offset-xl-5"
            @submit.prevent="processPasswordForm"
        >
            <div class="mb-4">
                <label class="visually-hidden form-label">Email</label>
                <input
                    ref="forgotPasswordEmail"
                    v-model="loginFormData.email"
                    autocomplete="false"
                    class="form-control input-lg"
                    name="email"
                    placeholder="Email"
                    type="text"
                >
            </div>

            <div class="mb-4">
                <button class="btn btn-primary w-100">Submit</button>
            </div>

            <div class="mb-4">
                <button class="btn btn-light btn-sm" type="button" @click="view = 'login'">« back</button>
            </div>
        </form>

        <div
            v-else-if="view === 'password-email-sent'"
            class="col-10 offset-1 col-sm-6 offset-sm-3 col-lg-4 offset-lg-4"
        >
            <div class="alert alert-info text-start">
                <h3 class="mt-0">Check your email</h3>
                <p>If you have entered a registered email address then you will shortly receive a reset password
                    email.</p>
                <p>If you can't find the email, you may need to check your spam folder.</p>
            </div>

            <p class="mb-4">
                <button class="btn btn-primary" type="button" @click="view = 'login'">Go to login</button>
            </p>
        </div>
    </section>
</template>